/**
 * SceneFxTunerPanel — live in-game tuner for the modular atmosphere layer
 * system (Scene Effects v2, E2). Reads the mounted scene's layers off the
 * `__atmosphere` singleton and lets you feel-call a scene without a redeploy:
 *
 *  • Scene-mood slider — instant global intensity dial (setSceneMood); the one
 *    knob that applies live every frame (reaches every modulatable layer).
 *  • Per-layer enable toggles — drop/restore a layer to isolate or compare it
 *    (edits the layer list + re-mounts via applyLayers).
 *  • Live modulator readout — shows each layer's modulators + the value the
 *    ticker is pushing right now (the "breathing" made visible).
 *  • Copy config — the active layers[] as JSON, to paste into a preset file.
 *
 * Edit-and-reapply (not poke-the-handle) so what you tweak is exactly what you
 * copy. Polls at 4Hz. Debug-surface-gated by the window framework.
 */
import React, { useCallback, useEffect, useState } from 'react';

/** The slice of the AtmosphereLayer singleton this panel uses. */
interface AtmosphereApi {
  activePreset(): string | null;
  spriteCount(): number;
  softLightCount(): number;
  getSceneMood(): number;
  setSceneMood(v: number): void;
  getActiveLayers(): AtmoLayer[];
  applyLayers(layers: AtmoLayer[]): void;
  modTracks?: { mods?: { target: string; shape: string }[]; handle?: { setDensity?: unknown; setOpacity?: unknown } }[];
}
interface AtmoLayer {
  kind: string;
  enabled?: boolean;
  opacity?: number;
  textureKey?: string;
  modulators?: { target: string; shape: string; periodMs: number; min: number; max: number }[];
  [k: string]: unknown;
}

const getAtmo = (): AtmosphereApi | null => {
  const a = (globalThis as { __atmosphere?: AtmosphereApi }).__atmosphere;
  return a && typeof a.getActiveLayers === 'function' ? a : null;
};

export const SceneFxTunerPanel: React.FC = () => {
  const [preset, setPreset] = useState<string | null>(null);
  const [layers, setLayers] = useState<AtmoLayer[]>([]);
  const [counts, setCounts] = useState({ sprites: 0, lights: 0, modTracks: 0 });
  const [mood, setMood] = useState(1);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    const a = getAtmo();
    if (!a) { setPreset(null); setLayers([]); return; }
    setPreset(a.activePreset());
    setLayers(a.getActiveLayers());
    setCounts({ sprites: a.spriteCount(), lights: a.softLightCount(), modTracks: a.modTracks?.length ?? 0 });
  }, []);

  useEffect(() => {
    const a = getAtmo();
    if (a) setMood(a.getSceneMood());
    refresh();
    const id = setInterval(refresh, 250);
    return () => clearInterval(id);
  }, [refresh]);

  const onMood = (v: number) => {
    setMood(v);
    getAtmo()?.setSceneMood(v);
  };

  const toggleLayer = (idx: number) => {
    const a = getAtmo();
    if (!a) return;
    const edited = a.getActiveLayers();
    if (!edited[idx]) return;
    edited[idx].enabled = edited[idx].enabled === false; // flip (undefined→true means currently on→off)
    a.applyLayers(edited);
    refresh();
  };

  const setLayerOpacity = (idx: number, value: number) => {
    const a = getAtmo();
    if (!a) return;
    const edited = a.getActiveLayers();
    if (!edited[idx]) return;
    edited[idx].opacity = value;
    a.applyLayers(edited);
    refresh();
  };

  const copyConfig = () => {
    const a = getAtmo();
    if (!a) return;
    const json = JSON.stringify(a.getActiveLayers(), null, 2);
    try {
      void navigator.clipboard?.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — no-op */ }
  };

  if (!preset) {
    return (
      <div style={S.empty}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>🌫</div>
        <div>No atmosphere mounted.</div>
        <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>Enter a scene with a preset (biome / lobby / nexus).</div>
      </div>
    );
  }

  return (
    <div style={S.container}>
      <div style={S.summary}>
        <span style={{ color: '#8ad' }}>{preset}</span>
        <span style={{ color: '#777' }}>· {counts.sprites} spr · {counts.lights} light · {counts.modTracks} mod</span>
      </div>

      {/* Scene mood — the one live global knob */}
      <div style={S.moodRow}>
        <span style={S.moodLabel}>Scene mood</span>
        <input type="range" min={0} max={2} step={0.05} value={mood}
          onChange={(e) => onMood(parseFloat(e.target.value))} style={{ flex: 1 }} />
        <span style={S.moodVal}>{mood.toFixed(2)}×</span>
        {mood !== 1 && <button style={S.resetBtn} onClick={() => onMood(1)}>reset</button>}
      </div>

      {/* Layers */}
      <div style={S.list}>
        {layers.map((l, i) => {
          const on = l.enabled !== false;
          const op = l.opacity ?? 1;
          return (
            <div key={i} style={{ ...S.layer, opacity: on ? 1 : 0.45 }}>
              <label style={S.layerHead}>
                <input type="checkbox" checked={on} onChange={() => toggleLayer(i)} />
                <span style={S.kind}>{l.kind}</span>
                {l.textureKey && <span style={S.tag}>{l.textureKey}</span>}
                {Array.isArray(l.modulators) && l.modulators.map((m, mi) => (
                  <span key={mi} style={S.mod}>{m.target}:{m.shape} {m.min}–{m.max}</span>
                ))}
              </label>
              <div style={S.opacityRow}>
                <span style={S.opacityLabel}>opacity</span>
                <input type="range" min={0} max={1} step={0.05} value={op}
                  onChange={(e) => setLayerOpacity(i, parseFloat(e.target.value))} style={{ flex: 1 }} />
                <span style={S.opacityVal}>{op.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={S.footer}>
        <button style={S.copyBtn} onClick={copyConfig}>{copied ? '✓ copied' : 'Copy config JSON'}</button>
        <span style={{ color: '#555', fontSize: 10 }}>edit + re-apply; paste into a preset</span>
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: 6, padding: 8, fontFamily: 'monospace', fontSize: 12, color: '#ccc', height: '100%', overflowY: 'auto' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120, color: '#666', fontSize: 12, fontFamily: 'monospace' },
  summary: { display: 'flex', gap: 8, padding: '4px 6px', background: 'rgba(0,0,0,0.3)', borderRadius: 4, alignItems: 'center', fontSize: 12 },
  moodRow: { display: 'flex', gap: 8, alignItems: 'center', padding: '4px 2px' },
  moodLabel: { color: '#aaa', minWidth: 72 },
  moodVal: { color: '#eee', minWidth: 40, textAlign: 'right' },
  resetBtn: { background: 'transparent', border: '1px solid #555', borderRadius: 3, color: '#aaa', fontSize: 10, cursor: 'pointer', padding: '1px 5px' },
  list: { display: 'flex', flexDirection: 'column', gap: 2 },
  layer: { padding: '5px 6px', background: 'rgba(255,255,255,0.03)', borderRadius: 4, borderLeft: '3px solid #345' },
  layerHead: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexWrap: 'wrap' },
  kind: { fontWeight: 'bold', color: '#eee' },
  opacityRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, paddingLeft: 20 },
  opacityLabel: { color: '#888', fontSize: 10, minWidth: 46 },
  opacityVal: { color: '#ddd', fontSize: 10, minWidth: 30, textAlign: 'right' },
  tag: { background: 'rgba(120,160,220,0.18)', border: '1px solid #46a', borderRadius: 3, padding: '0 5px', fontSize: 10, color: '#9cf' },
  mod: { background: 'rgba(220,160,120,0.15)', border: '1px solid #a64', borderRadius: 3, padding: '0 5px', fontSize: 10, color: '#fc9' },
  footer: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 },
  copyBtn: { background: 'rgba(80,160,120,0.25)', border: '1px solid #4a7', borderRadius: 4, color: '#cfe', fontSize: 11, cursor: 'pointer', padding: '3px 8px' },
};
